import { useState, useEffect, useMemo } from "react";
import { useTable, useSortBy, useFilters } from "react-table";
import { Container, Row, Col, Table } from "react-bootstrap";
import Money from "../Parts/Money";
import { FaArrowUp, FaArrowDown, FaPlusCircle, FaSyncAlt, FaMinusCircle, FaTimes, FaExclamationCircle, FaTicketAlt, FaBarcode, FaTruck } from 'react-icons/fa';
import { useQuoteById, useQuoteTypeNameByQuoteId } from "../Store/entities/quotes/hooks";
import apiCalls from "../PowerShadesAPIFunctions";



// Simulated function to get quote type
function getQuoteType(quote) { 
  if (!quote) return 'default';
  return quote.type === 'premium' ? 'premium' : 'standard';
}
const QuoteHistoryHeadRow = (props) => (
	<tr className="bold-row">
		<th>Date / Time</th>
		<th>Description</th>
		<th>User Name</th>
		<th>User Email</th> 
		<th>Price Snapshot</th>
	</tr>
);

// Define a default column filter for text filtering
function DefaultColumnFilter({
  column: { filterValue, setFilter },
}) {
  return (
    <input
      value={filterValue || ""}
      onClick={(e) => e.stopPropagation()} // Evitar que se active el ordenamiento al hacer clic en el campo de búsqueda
      onChange={(e) => setFilter(e.target.value || undefined)}
      placeholder={`Search...`}
      style={{ width: '100%' }}
    />
  );
}

// Add / Delete entries arrive as a serialized anonymous object, e.g.
// "Added Awning { SequenceId = 3 }". Rewrite them into the same "Key: Value"
// shape the field-change entries use so the renderer below handles both.
const braceObjectPattern = /^(.*?)\s*\{\s*(.*?)\s*\}$/;
const normalizeBraceObject = (description) => {
  const match = braceObjectPattern.exec(description.trim());

  if (!match) return description;

  const [, prefix, body] = match;
  const fields = body.split(",").map((field) => field.trim()).filter((field) => field !== "");

  // Only rewrite genuine "Name = Value" pairs, so a description that happens to
  // carry a JSON-ish payload is left alone.
  if (fields.length === 0 || !fields.every((field) => /^\w+\s*=/.test(field))) {
    return description;
  }

  return [prefix, ...fields.map((field) => field.replace(/\s*=\s*/, ": "))]
    .filter((part) => part !== "")
    .join(", ");
};

// Function to format the description field (with icons and colors)
// `sequenceLabel` lets callers relabel SequenceId — on an awnings quote it is a
// line number rather than an assembly sequence id.
const formatDescription = (description, sequenceLabel = "Sequence Id") => {
  if (typeof description !== "string") {
    console.warn("Warning: formatDescription received a non-string value:", description);
    description = String(description || "");
  }

  if (description.includes("AddAssemblyLog")) {
    return <div>{description}</div>;
  }

  let formattedDescription = normalizeBraceObject(description)
    .split(/, |; /)
    .map((line, index) => {
      line = line
        .replace("SequenceId", sequenceLabel)
        .replace("FieldName", "Field Name")
        .replace("OldValue", "Previous Value")
        .replace("NewValue", "Current Value")
        .replace("RowId", "Row Id")
        .replace("ColumnId", "Column Id");

      if (line.includes("Row Id:") && (line.includes("Row Id: ,") || line.trim().endsWith("Row Id:"))) {
        return null;
      }
      if (line.includes("Column Id:") && (line.includes("Column Id: ,") || line.trim().endsWith("Column Id:"))) {
        return null;
      }
    //  if (line.includes("Old Value:") && (line.includes("Old Value: ,") || line.trim().endsWith("Old Value:"))) {
    //    return null;
    //  }
    //  if (line.includes("New Value:") && (line.includes("New Value: ,") || line.trim().endsWith("New Value:"))) {
    //    return null;
    //  }

      const icon = line.includes("Added") ? <FaPlusCircle style={{ color: "blue" }} /> :
                   line.includes("Created") && !line.includes("Created Shipment") ? <FaPlusCircle style={{ color: "green" }} /> :
                   line.includes("Error") ? <FaExclamationCircle style={{ color: "red" }} /> : 
                   line.includes("error") ? <FaExclamationCircle style={{ color: "red" }} /> : 
                   line.includes("Coupon") ? <FaTicketAlt style={{ color: "green" }} /> : 
                   line.includes("Recipe SKU") ? <FaBarcode /> : 
                   line.includes("Created Shipment") ? <FaTruck /> : 
                   line.includes("PowerShades Admin unlocked") ? <FaExclamationCircle style={{ color: "orange" }} /> :
                   line.includes("Duplicated Quote") ? <FaPlusCircle style={{ color: "green" }} /> :
                   line.includes("Updated") ? <FaSyncAlt style={{ color: "green" }} /> :
                   line.includes("Removed") ? <FaMinusCircle style={{ color: "red" }} /> :
                   line.includes("Deleted") ? <FaMinusCircle style={{ color: "red" }} /> : null;

                   

      const parts = line.split(": ");
      const style = index === 0 ? {} : { marginLeft: "20px" };

      if (parts.length > 1) {
        return (
          <div key={index} style={style}>
            {icon} <strong style={{ color: "darkgreen" }}>{parts[0]}:</strong> {parts[1]}
          </div>
        );
      } else {
        return (
          <div key={index} style={style}>
            {icon} {line} 
          </div>
        );
      }
    })
    .filter(line => line !== null);

  return formattedDescription;
};

// Define the columns and the first table (Quote History)
// Awnings live in this same table — the backend logs them into shade_history —
// so the labels flip to awning wording and Indoor / Outdoor (always null for an
// awning) is dropped entirely.
const QuoteHistoryTable = ({ entries, quoteType, isAwningHistory }) => {
  const sequenceLabel = isAwningHistory ? "Line" : "Sequence Id";

  const columns = useMemo(
    () => [
      {
        Header: "Date / Time",
        accessor: "entry_time",
        disableFilters: true,
      },
      {
        Header: isAwningHistory ? "Line #" : "Seq Id",
        accessor: "sequence_id",
        Filter: DefaultColumnFilter,
      },
      {
        Header: isAwningHistory ? "Awning Name" : "Shade Name",
        accessor: "shade_name",
        Filter: DefaultColumnFilter,
      },
      {
        Header: isAwningHistory ? "Type" : "Shade Type",
        accessor: "shade_type",
        Filter: DefaultColumnFilter,
      },
      ...(isAwningHistory
        ? []
        : [
          {
            Header: "Indoor / Outdoor",
            id: "indoor_outdoor",
            // Awning rows send null here; normalize so sorting and the text
            // filter don't have to deal with it.
            accessor: (row) => row.indoor_outdoor ?? "",
            Filter: DefaultColumnFilter,
          },
        ]),
      {
        Header: "Description",
        accessor: "description",
        Cell: ({ value }) => formatDescription(value, sequenceLabel),
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Price Snapshot",
        accessor: "msrp_snapshot",
        Cell: ({ value }) => <Money>{value}</Money>,
        disableFilters: true,
      },
      {
        Header: "User Name",
        accessor: "user_name",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "User Email",
        accessor: "user_email",
        Filter: DefaultColumnFilter,
      },
    ],
    [isAwningHistory, sequenceLabel]
  );

  const tableInstance = useTable(
    {
      columns,
      data: entries,
      defaultColumn: { Filter: DefaultColumnFilter },
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  return (
    <>
      <Row>
        <Col>
          <div className="sorting-info" style={{ marginBottom: '10px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '5px' }}>
            <p style={{ margin: 0 }}>
              Click on the column headers to sort the data. You can toggle between ascending and descending order by clicking the arrows.
              <span style={{ display: 'inline-block', marginLeft: '10px' }}>
                <FaArrowUp /> <FaArrowDown />
              </span>
            </p>
          </div>
        </Col>
      </Row>
      <Table {...getTableProps()} responsive size="sm" bordered hover className={`table-striped-rows ${quoteType}`}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ backgroundColor: '#8dc640', color: 'white', fontWeight: 'bold' }}
                >
                  {column.render("Header")}
                  <span style={{ fontSize: "12px", color: 'darkgreen' }}>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <FaArrowDown />
                      ) : (
                        <FaArrowUp />
                      )
                    ) : ""}
                  </span>
                  <div>{column.canFilter ? column.render("Filter") : null}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};

// Define the columns and the second table (Accessories)
const AccessoriesTable = ({ accessories }) => {
  const columns = useMemo(
    () => [
      {
        Header: "Date / Time",
        accessor: "entry_time",
        disableFilters: true,
      },
      {
        Header: "Action",
        accessor: "action",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => formatDescription(value)
      },
      {
        Header: "Part Number",
        accessor: "part_number",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Name",
        accessor: "name",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Reference",
        accessor: "field_name",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Current Value",
        accessor: "new_value",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Previous Value",
        accessor: "old_value",
        Filter: DefaultColumnFilter,
      },  
      {
        Header: "User Name",
        accessor: "user_name",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "User Email",
        accessor: "user_email",
        Filter: DefaultColumnFilter,
      },
    ],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data: accessories,
      defaultColumn: { Filter: DefaultColumnFilter },
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  return (
    <>
      <Row>
        <Col>
          <div className="sorting-info" style={{ marginBottom: '10px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '5px' }}>
            <p style={{ margin: 0 }}>
              Click on the column headers to sort the data. You can toggle between ascending and descending order by clicking the arrows.
              <span style={{ display: 'inline-block', marginLeft: '10px' }}>
                <FaArrowUp /> <FaArrowDown />
              </span>
            </p>
          </div>
        </Col>
      </Row>
      <Table {...getTableProps()} responsive size="sm" bordered hover className={`table-striped-rows`}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ backgroundColor: '#8dc640', color: 'white', fontWeight: 'bold' }}
                >
                  {column.render("Header")}
                  <span style={{ fontSize: "12px", color: 'darkgreen' }}>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <FaArrowDown />
                      ) : (
                        <FaArrowUp />
                      )
                    ) : ""}
                  </span>
                  <div>{column.canFilter ? column.render("Filter") : null}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};

// Define the columns and the third table (Hardware Requests)
const HardwareRequestsTable = ({ hardwareRequests }) => {
  const columns = useMemo(
    () => [
      {
        Header: "Date / Time",
        accessor: "entry_time",
        disableFilters: true,
      },
      {
        Header: "Action",
        accessor: "action",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => formatDescription(value)
      },
      {
        Header: "SKU",
        accessor: "part_number",
        Filter: DefaultColumnFilter,
      },

      {
        Header: "Description",
        accessor: "description",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Unit Measurement",
        accessor: "unit_of_measurement",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Current Value",
        accessor: "current_value",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Previous Value",
        accessor: "previous_value",
        Filter: DefaultColumnFilter,
      },  
      {
        Header: "User Name",
        accessor: "user_name",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "User Email",
        accessor: "user_email",
        Filter: DefaultColumnFilter,
      },
    ],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data: hardwareRequests,
      defaultColumn: { Filter: DefaultColumnFilter },
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  return (
    <>
      <Row>
        <Col>
          {/* Message to inform user about sorting functionality */}
          <div className="sorting-info" style={{ marginBottom: '10px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '5px' }}>
            <p style={{ margin: 0 }}>
              Click on the column headers to sort the data. You can toggle between ascending and descending order by clicking the arrows.
              <span style={{ display: 'inline-block', marginLeft: '10px' }}>
                <FaArrowUp /> <FaArrowDown />
              </span>
            </p>
          </div>
        </Col>
      </Row>
      <Table {...getTableProps()} responsive size="sm" bordered hover className={`table-striped-rows`}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ backgroundColor: '#8dc640', color: 'white', fontWeight: 'bold' }}
                >
                  {column.render("Header")}
                  <span style={{ fontSize: "12px", color: 'darkgreen' }}>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <FaArrowDown />
                      ) : (
                        <FaArrowUp />
                      )
                    ) : ""}
                  </span>
                  <div>{column.canFilter ? column.render("Filter") : null}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};

// Define the columns and the fourth table (General Quotes Actions)
const GeneralQuotesActionsTable = ({ generalActions }) => {
  const columns = useMemo(
    () => [
      {
        Header: "Date / Time",
        accessor: "entry_time",
        disableFilters: true,
      },
      {
        Header: "Action",
        accessor: "action",
        Filter: DefaultColumnFilter,
        Cell: ({ value }) => formatDescription(value)
      },
      {
        Header: "Description",
        accessor: "description",
        Cell: ({ value }) => formatDescription(value),
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Current Value",
        accessor: "current_value",
        Cell: ({ value }) => formatDescription(value),
        Filter: DefaultColumnFilter,
      },
      {
        Header: "Previous Value",
        accessor: "old_value",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "User Name",
        accessor: "user_name",
        Filter: DefaultColumnFilter,
      },
      {
        Header: "User Email",
        accessor: "user_email",
        Filter: DefaultColumnFilter,
      },
    ],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data: generalActions,
      defaultColumn: { Filter: DefaultColumnFilter },
    },
    useFilters,
    useSortBy
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = tableInstance;

  return (
    <>
      <Row>
        <Col>
          {/* Message to inform user about sorting functionality */}
          <div className="sorting-info" style={{ marginBottom: '10px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '5px' }}>
            <p style={{ margin: 0 }}>
              Click on the column headers to sort the data. You can toggle between ascending and descending order by clicking the arrows.
              <span style={{ display: 'inline-block', marginLeft: '10px' }}>
                <FaArrowUp /> <FaArrowDown />
              </span>
            </p>
          </div>
        </Col>
      </Row>
      <Table {...getTableProps()} responsive size="sm" bordered hover className={`table-striped-rows`}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ backgroundColor: '#8dc640', color: 'white', fontWeight: 'bold' }}
                >
                  {column.render("Header")}
                  <span style={{ fontSize: "12px", color: 'darkgreen' }}>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <FaArrowDown />
                      ) : (
                        <FaArrowUp />
                      )
                    ) : ""}
                  </span>
                  <div>{column.canFilter ? column.render("Filter") : null}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};

const excludedDescriptions = [
  "This order was set to ship",
  "The salesperson was set to",
  "This order was set to pre ship whips",
  "This order was set to not ship",
  "The title was set to",
  "The reference number was set to",
  "The notes were set to",
  "This order was set to pre ship whip",
  "This order was set to not pre ship whips",
  "This order was not set to have constant balanced lightgaps",
  "This order was set to have constant balanced lightgaps",
  "The rma type was set to",
  "Notes Updated",
  "previous_quote_id",
  "updated was changed",
  "Coupon named",
  "This quote has been submitted",
  "Assigned Manufacturer",
  "Dashboard Populated Succesfully",
  "Dashboard Populated Failed", 
  "Recipe SKU",
  "Retainer Invoice Creation Succeded",
  "Created Shipment"
];


const historyKeys = ["quote_history", "accessory_history", "hardware_history", "shade_history"];

const noticeStyle = {
  marginTop: '30px',
  backgroundColor: '#f5f5f5',
  padding: '10px',
  borderRadius: '5px',
};

const asRows = (value) => (Array.isArray(value) ? value : []);

// On a failed request apiGet hands back the server's error body as `data` and
// rewrites the status to 400, so the real cause is in the payload rather than in
// the axios error message (which is only ever "Request failed with status code N").
const serverErrorDetail = (resp) => {
  const body = resp.data;

  if (typeof body === "string" && body.trim() !== "") return body;

  const message = body?.message ?? body?.detail ?? body?.title ?? body?.error
    ?? body?.exceptionMessage ?? body?.Message;

  if (typeof message === "string" && message.trim() !== "") return message;

  return resp.error?.message ?? null;
};

const QuoteHistory = (props) => {

  const [history, setHistory] = useState(null);
  // apiGet never rejects — on a failed request it resolves with a null payload
  // and status 400, so the response has to be checked explicitly or the modal
  // renders empty and looks identical to "this quote has no history".
  const [loadState, setLoadState] = useState({ status: "loading" });

  const entries = asRows(history?.shade_history);
  const accessories = asRows(history?.accessory_history);
  const hardwareRequests = asRows(history?.hardware_history);
  const generalActions = asRows(history?.quote_history);

  const hasAnyHistory =
    entries.length + accessories.length + hardwareRequests.length + generalActions.length > 0;

  const quoteType = useQuoteTypeNameByQuoteId(props.quoteId);
  const quote = useQuoteById(props.quoteId);

  // Awnings are quote accessories, not assemblies, but the backend logs them
  // into shade_history all the same. Prefer the quote's product category (how
  // the rest of the portal tells the two apart) and fall back to the rows
  // themselves when the quote isn't loaded in the store yet.
  const isAwningQuote = (quote?.product_category ?? 'roller_shades') === 'awnings';
  const isAwningHistory =
    isAwningQuote
    || (entries.length > 0 && entries.every((entry) => entry.shade_type === "Awning"));

  // Load accessories data history
  useEffect(() => {
    let cancelled = false;

    setLoadState({ status: "loading" });
    setHistory(null);

    apiCalls.getQuoteHistory(props.quoteId).then((resp) => {
      if (cancelled) return;

      if (resp.status !== 200 || !resp.data) {
        console.error("Quote history request failed.", { quoteId: props.quoteId, resp });
        setLoadState({
          status: "error",
          // apiGet overwrites status with 400; the real code is on the axios error.
          httpStatus: resp.error?.response?.status ?? resp.status,
          detail: serverErrorDetail(resp),
        });
        return;
      }

      // A response that carries none of the expected arrays means the payload
      // shape changed (casing, envelope) rather than the quote being empty.
      if (historyKeys.every((key) => !Array.isArray(resp.data[key]))) {
        console.error("Quote history response has an unexpected shape.", {
          quoteId: props.quoteId,
          keys: Object.keys(resp.data),
          data: resp.data,
        });
        setLoadState({ status: "bad-shape", keys: Object.keys(resp.data) });
        return;
      }

      setHistory(resp.data);
      setLoadState({ status: "loaded" });
    });

    return () => {
      cancelled = true;
    };
  },[props.quoteId]);

  return (
    <div className="modal-holder">
      <div className="quote-history-modal">
        <Container className="local-bootstrap">
          {/* Botón de cierre en la esquina superior derecha */}
          <button 
            onClick={props.onClose} 
            style={{ 
              position: 'absolute', 
              top: '10px', 
              right: '10px', 
              background: 'none', 
              border: 'none', 
              fontSize: '20px', 
              cursor: 'pointer',
              color: '#000'
            }}
            aria-label="Close"
          >
            <FaTimes />
          </button>
          
          <Row>
            <Col>
              <h4 className={`text-${quoteType}`}>Quote History</h4>
            </Col>
          </Row>
  
          {loadState.status === "loading" && (
            <Row>
              <Col>
                <p style={{ marginTop: '30px' }}>Loading quote history&hellip;</p>
              </Col>
            </Row>
          )}

          {loadState.status === "error" && (
            <Row>
              <Col>
                <div style={noticeStyle}>
                  <FaExclamationCircle style={{ color: "red" }} />{" "}
                  Couldn&apos;t load the history for quote {props.quoteId}
                  {loadState.httpStatus ? ` (HTTP ${loadState.httpStatus})` : ""}. The request
                  failed, so this is not the same as the quote having no history.
                  {loadState.detail ? (
                    <div style={{ marginTop: '8px', fontFamily: 'monospace', wordBreak: 'break-word' }}>
                      {loadState.detail}
                    </div>
                  ) : null}
                </div>
              </Col>
            </Row>
          )}

          {loadState.status === "bad-shape" && (
            <Row>
              <Col>
                <div style={noticeStyle}>
                  <FaExclamationCircle style={{ color: "red" }} />{" "}
                  The history response for quote {props.quoteId} didn&apos;t contain any of the
                  expected lists ({historyKeys.join(", ")}). It returned:{" "}
                  {loadState.keys.length > 0 ? loadState.keys.join(", ") : "no fields at all"}.
                </div>
              </Col>
            </Row>
          )}

          {loadState.status === "loaded" && !hasAnyHistory && (
            <Row>
              <Col>
                <div style={noticeStyle}>
                  No history has been recorded for quote {props.quoteId}.
                </div>
              </Col>
            </Row>
          )}

          {/* Conditionally render General Quotes Actions section */}
          {generalActions.length > 0 && (
            <>
              <Row>
                <Col>
                  <h5 className={`text-${quoteType}`} style={{ marginTop: '30px' }}>General Quote Actions</h5>
                </Col>
              </Row>
              <Row>
                <Col>
                  <GeneralQuotesActionsTable generalActions={generalActions} />
                </Col>
              </Row>
            </>
          )}

          {loadState.status === "loaded" && hasAnyHistory && (
            <>
              <Row>
                <Col>
                  <h5 className={`text-${quoteType}`} style={{ marginTop: '30px' }}>
                    {isAwningHistory ? "Awnings" : "Shades"}
                  </h5>
                </Col>
              </Row>

              <Row>
                <Col>
                  <QuoteHistoryTable
                    quoteType={quoteType}
                    entries={entries}
                    isAwningHistory={isAwningHistory}
                  />
                </Col>
              </Row>
            </>
          )}


          {/* Conditionally render Accessories section */}
          {accessories.length > 0 && (
            <>
              <Row>
                <Col>
                  <h5 className={`text-${quoteType}`} style={{ marginTop: '30px' }}>Accessories</h5>
                </Col>
              </Row>
              <Row>
                <Col>
                  <AccessoriesTable accessories={accessories} />
                </Col>
              </Row>
            </>
          )}

          {/* Conditionally render Hardware Requests section */}
          {hardwareRequests.length > 0 && (
            <>
              <Row>
                <Col>
                  <h5 className={`text-${quoteType}`} style={{ marginTop: '30px' }}>Hardware Requests</h5>
                </Col>
              </Row>
              <Row>
                <Col>
                  <HardwareRequestsTable hardwareRequests={hardwareRequests} />
                </Col>
              </Row>
            </>
          )}

          <div className="button-holder">
            <button
              style={{ paddingLeft: "16px", paddingRight: "16px" }}
              type="button"
              className={`btn btn-green pt-0 pb-0 btn-${quoteType}`}
              onClick={props.onClose}
            >
              Done
            </button>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default QuoteHistory;
